import * as THREE from "three";
import navigationHotspotSvg from "../../../assets/hotspot-navigation.svg";
import labelHotspotSvg from "../../../assets/hotspot-label.svg";
import {
  createPanoramaRuntime,
  estimateTextureBytes,
  getInitialPixelRatio,
  getMaxPanoramaWidth,
  getTextureByteBudget,
} from "./panoramaPerf";
import { getHotspotType, HOTSPOT_TYPES, isTextHotspot } from "./hotspotTypes";

export {
  createQualityController,
  getInitialPixelRatio,
  getLinkedSceneUrls,
  getMaxPanoramaWidth,
  getTextureByteBudget,
  normalizePanoramaUrl,
  PRIORITY,
  shouldApplyLoad,
  shouldEnableAntialias,
  TOUR_PERF,
  velocityOnRelease,
} from "./panoramaPerf";

export const PANORAMA_RADIUS = 500;

export const DEFAULT_FOV = 75;
export const MIN_FOV = 40;
export const MAX_FOV = 90;
export const MAX_FOV_180 = 80;
export const MAX_HFOV_180_DEG = 112;

const HEMISPHERE_EDGE_MARGIN_RAD = THREE.MathUtils.degToRad(0.75);
const MAX_PITCH_TANGENT_SHARE_180 = 0.75;
const PITCH_LIMIT_180_RAD = THREE.MathUtils.degToRad(65);

export const yawPitchToVector3 = (yaw, pitch, radius = PANORAMA_RADIUS) => {
  const x = radius * Math.cos(pitch) * Math.sin(yaw);
  const y = radius * Math.sin(pitch);
  const z = -radius * Math.cos(pitch) * Math.cos(yaw);
  return new THREE.Vector3(x, y, z);
};

export const vector3ToYawPitch = (point, radius = PANORAMA_RADIUS) => {
  const yaw = Math.atan2(point.x, -point.z);
  const pitch = Math.asin(THREE.MathUtils.clamp(point.y / radius, -1, 1));
  return { yaw, pitch };
};

export const getPixelRatio = () =>
  getInitialPixelRatio({
    devicePixelRatio: typeof window !== "undefined" ? window.devicePixelRatio : 1,
    deviceMemory: typeof navigator !== "undefined" ? navigator.deviceMemory : undefined,
  });

export const createPanoramaGeometry = (
  projection = "equirectangular_360",
  { thetaStart = 0, thetaLength = Math.PI } = {}
) => {
  if (projection === "equirectangular_180") {
    // In Three.js SphereGeometry, scaled by (-1, 1, 1):
    // phiStart = Math.PI and phiLength = Math.PI spans from -X (-90 deg yaw, u=0)
    // across -Z (0 deg yaw, u=0.5) to +X (+90 deg yaw, u=1.0).
    const geometry = new THREE.SphereGeometry(
      PANORAMA_RADIUS,
      64,
      48,
      Math.PI,
      Math.PI,
      thetaStart,
      thetaLength
    );
    geometry.scale(-1, 1, 1);
    return geometry;
  }

  const geometry = new THREE.SphereGeometry(PANORAMA_RADIUS, 48, 32);
  geometry.scale(-1, 1, 1);
  return geometry;
};

const get180FrustumTangents = camera => {
  const safeAspect = Math.max(camera?.aspect || 1, 0.1);
  const vFovRad = THREE.MathUtils.degToRad(camera?.fov || DEFAULT_FOV);
  const halfVFov = Math.min(Math.PI / 2 - 0.02, vFovRad / 2);
  const halfHFov = Math.min(
    Math.PI / 2 - 0.02,
    Math.atan(safeAspect * Math.tan(halfVFov))
  );
  const effHalfV = Math.min(
    Math.PI / 2 - 0.01,
    halfVFov + HEMISPHERE_EDGE_MARGIN_RAD
  );
  const effHalfH = Math.min(
    Math.PI / 2 - 0.01,
    halfHFov + HEMISPHERE_EDGE_MARGIN_RAD
  );
  const tanV = Math.max(0.01, Math.tan(effHalfV));
  const tanH = Math.max(0.01, Math.tan(effHalfH));
  const maxPitchByLimit = tanV * Math.tan(PITCH_LIMIT_180_RAD);
  const maxV = Math.min(MAX_PITCH_TANGENT_SHARE_180, maxPitchByLimit);
  return {
    tanH,
    tanV,
    maxV,
    halfHFov,
    halfVFov,
  };
};

export const get180CameraPitchRange = camera => {
  if (!camera || !camera.aspect) {
    return {
      min: 0,
      max: 0,
      maxPitch: 0,
      halfVFov: 0,
    };
  }
  const { tanV, maxV, halfVFov } = get180FrustumTangents(camera);
  const maxPitch = Math.atan(maxV / tanV);
  return {
    min: -maxPitch,
    max: maxPitch,
    maxPitch,
    halfVFov,
  };
};

export const get180CameraYawRange = (camera, centerOffsetRad = 0, latRad = 0) => {
  if (!camera || !camera.aspect) {
    return {
      min: centerOffsetRad,
      max: centerOffsetRad,
      maxCenterYaw: 0,
      halfHFov: 0,
      overflowsCoverage: false,
    };
  }
  const { tanH, tanV, maxV, halfHFov } = get180FrustumTangents(camera);
  const maxPitch = Math.atan(maxV / tanV);
  const clampedPitch = THREE.MathUtils.clamp(latRad || 0, -maxPitch, maxPitch);
  const vShare = Math.min(maxV, tanV * Math.abs(Math.tan(clampedPitch)));
  const availU = Math.max(0, 1 - vShare);
  const maxCenterYaw = Math.atan(availU / tanH);




  return {
    min: centerOffsetRad - maxCenterYaw,
    max: centerOffsetRad + maxCenterYaw,
    maxCenterYaw,
    halfHFov,
    overflowsCoverage: availU <= 0,
  };
};

export const clampYaw180 = (lon, camera, centerOffsetRad = 0, latRad = 0) => {
  const { min, max } = get180CameraYawRange(camera, centerOffsetRad, latRad);
  return THREE.MathUtils.clamp(lon, min, max);
};

export const clampPitch180 = (lat, camera) => {
  const { min, max } = get180CameraPitchRange(camera);
  return THREE.MathUtils.clamp(lat, min, max);
};

export const clampLook180 = (
  lon,
  lat,
  camera,
  centerOffsetRad = 0,
  { mode = "static" } = {}
) => {
  if (!camera || !camera.aspect) {
    return {
      lon: centerOffsetRad,
      lat: 0,
      clampedLon: true,
      clampedLat: true,
    };
  }
  const { tanH, tanV, maxV } = get180FrustumTangents(camera);
  const maxAngle = Math.PI / 2 - 0.001;
  const relLon = THREE.MathUtils.clamp(
    (lon ?? 0) - centerOffsetRad,
    -maxAngle,
    maxAngle
  );
  const safeLat = THREE.MathUtils.clamp(lat ?? 0, -maxAngle, maxAngle);

  const rawU = tanH * Math.tan(relLon);
  const rawV = tanV * Math.tan(safeLat);

  let u = rawU;
  let v = THREE.MathUtils.clamp(rawV, -maxV, maxV);
  let clampedLat = Math.abs(v - rawV) > 1e-6;
  let clampedLon = false;

  const absU = Math.abs(u);
  const absV = Math.abs(v);

  if (absU + absV > 1) {
    if (mode === "drag") {
      const delta = (absU + absV - 1) * 0.5;
      const nextAbsV = THREE.MathUtils.clamp(absV - delta, 0, maxV);
      const nextAbsU = THREE.MathUtils.clamp(1 - nextAbsV, 0, 1);
      clampedLon = Math.abs(nextAbsU - absU) > 1e-6;
      clampedLat = clampedLat || Math.abs(nextAbsV - absV) > 1e-6;
      u = Math.sign(u) * nextAbsU;
      v = Math.sign(v) * nextAbsV;
    } else {
      const nextAbsU = Math.max(0, 1 - absV);
      clampedLon = absU > nextAbsU + 1e-6;
      u = Math.sign(u) * Math.min(absU, nextAbsU);
    }
  }

  return {
    lon: centerOffsetRad + Math.atan(u / tanH),
    lat: Math.atan(v / tanV),
    clampedLon,
    clampedLat,
  };
};

export const getMaxFovFor180 = (
  aspect,
  maxHFovDeg = MAX_HFOV_180_DEG,
  maxVFovDeg = MAX_FOV_180
) => {
  const maxHalfHFovRad = THREE.MathUtils.degToRad(maxHFovDeg / 2);
  const safeAspect = Math.max(aspect || 1, 0.1);
  const maxTanHalfV = Math.tan(maxHalfHFovRad) / safeAspect;
  const maxVRad = 2 * Math.atan(maxTanHalfV);
  const maxVDeg = THREE.MathUtils.radToDeg(maxVRad);
  return Math.max(MIN_FOV, Math.min(MAX_FOV, maxVFovDeg, maxVDeg));
};

export const createPanoramaMaterial = () => {
  const material = new THREE.MeshBasicMaterial({
    color: 0xffffff,
    depthWrite: true,
  });

  // Local backport of three.js #26521 for r152: upload video as RGBA and
  // decode sRGB in the shader, avoiding the costly SRGB8_ALPHA8 video path.
  // https://github.com/mrdoob/three.js/pull/26521
  const needsVideoDecode = () => Boolean(material.map?.userData?.panoramaVideoSRGBDecode);
  material.onBeforeCompile = shader => {
    if (!needsVideoDecode()) return;
    shader.fragmentShader = shader.fragmentShader.replace(
      "#include <map_fragment>",
      `#ifdef USE_MAP
        vec4 panoramaVideoSample = texture2D(map, vMapUv);
        panoramaVideoSample.rgb = mix(
          pow((panoramaVideoSample.rgb + vec3(0.055)) / 1.055, vec3(2.4)),
          panoramaVideoSample.rgb / 12.92,
          vec3(lessThanEqual(panoramaVideoSample.rgb, vec3(0.04045)))
        );
        diffuseColor *= panoramaVideoSample;
      #endif`
    );
  };
  // The two reusable panorama meshes can switch between images and videos.
  // Keep their shader variants distinct when assignMeshTexture recompiles.
  material.customProgramCacheKey = () =>
    needsVideoDecode() ? "panorama-video-srgb-decode-v1" : "panorama-image-v1";
  return material;
};

export const createPanoramaSphere = (geometry = createPanoramaGeometry()) => {
  const material = createPanoramaMaterial();
  const mesh = new THREE.Mesh(geometry, material);
  mesh.frustumCulled = false;
  mesh.visible = false;
  return { mesh, geometry, material };
};

const HOTSPOT_CANVAS_W = 768;
const HOTSPOT_CANVAS_H = 256;

// Hotspot appearance settings. Navigation and label hotspots can be adjusted
// independently here. Both cards intentionally use the same border radius.
// Connector controls:
// lineInclineX/Y = angled segment distance; lineStraightLength = horizontal segment.
const HOTSPOT_CARD_RADIUS = 14;
export const HOTSPOT_STYLES = {
  [HOTSPOT_TYPES.NAVIGATION]: {
    iconSrc: navigationHotspotSvg,
    accentColor: "#12dbe3",
    iconSize: 64,
    circleHitRadius: 30,
    circleX: 72,
    lineInclineX: 20,
    lineInclineY: -55,
    lineStraightLength: 40,
    lineWidth: 3,
    lineHitPadding: 5,
    cardHeight: 46,
    cardMinWidth: 10,
    cardMaxWidth: 420,
    cardPaddingX: 22,
    cardRadius: HOTSPOT_CARD_RADIUS,
    cardBackground: "#242636",
    // cardGradient: ["rgba(5, 5, 8, 0.96)", "rgba(42, 42, 48, 0.9)"],
    cardBorderColor: "rgba(18, 219, 227, 0.35)",
    cardBorderWidth: 1,
    fontSize: 20,
    fontColor: "#ffffff",
    fontFamily: "Outfit, Arial, sans-serif",
    fontWeight: "400",
    spriteWidth: 480,
    spriteHeight: 160,
  },
  [HOTSPOT_TYPES.TEXT]: {
    iconSrc: labelHotspotSvg,
    accentColor: "#12dbe3",
    iconSize: 64,
    circleHitRadius: 30,
    circleX: 72,
    lineInclineX: 20,
    lineInclineY: -55,
    lineStraightLength: 40,
    lineWidth: 3,
    lineHitPadding: 5,
    cardHeight: 46,
    cardMinWidth: 1,
    cardMaxWidth: 420,
    cardPaddingX: 22,
    cardRadius: HOTSPOT_CARD_RADIUS,
    cardBackground: "#242636",
    // cardGradient: ["rgba(5, 5, 8, 0.96)", "rgba(42, 42, 48, 0.9)"],
    cardBorderColor: "rgba(18, 219, 227, 0.35)",
    cardBorderWidth: 1,
    fontSize: 20,
    fontColor: "#ffffff",
    fontFamily: "Outfit, Arial, sans-serif",
    fontWeight: "400",
    spriteWidth: 480,
    spriteHeight: 160,
  },
};

const iconPromises = new Map();

const loadHotspotIcon = src => {
  if (!iconPromises.has(src)) {
    iconPromises.set(
      src,
      new Promise(resolve => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = () => resolve(null);
        img.src = src;
      })
    );
  }
  return iconPromises.get(src);
};

const getHotspotStyle = hotspot =>
  HOTSPOT_STYLES[getHotspotType(hotspot)] ?? HOTSPOT_STYLES[HOTSPOT_TYPES.NAVIGATION];

const roundedRect = (ctx, x, y, width, height, radius) => {
  const r = Math.min(radius, width / 2, height / 2);
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + width, y, x + width, y + height, r);
  ctx.arcTo(x + width, y + height, x, y + height, r);
  ctx.arcTo(x, y + height, x, y, r);
  ctx.arcTo(x, y, x + width, y, r);
  ctx.closePath();
};

const drawExternalLinkIcon = (ctx, x, y, color) => {
  ctx.save();
  ctx.strokeStyle = color;
  ctx.lineWidth = 2;
  ctx.lineCap = "round";
  ctx.lineJoin = "round";
  ctx.strokeRect(x, y + 5, 14, 14);
  ctx.beginPath();
  ctx.moveTo(x + 7, y + 12);
  ctx.lineTo(x + 19, y);
  ctx.moveTo(x + 11, y);
  ctx.lineTo(x + 19, y);
  ctx.lineTo(x + 19, y + 8);
  ctx.stroke();
  ctx.restore();
};

const wrapTextLines = (ctx, text, maxTextWidth) => {
  const rawParagraphs = text.split(/\r?\n/);
  const lines = [];

  for (const paragraph of rawParagraphs) {
    const trimmed = paragraph.trim();
    if (!trimmed) {
      if (lines.length > 0) lines.push("");
      continue;
    }

    if (ctx.measureText(trimmed).width <= maxTextWidth) {
      lines.push(trimmed);
      continue;
    }

    const words = trimmed.split(/\s+/);
    let currentLine = "";
    for (const word of words) {
      const testLine = currentLine ? `${currentLine} ${word}` : word;
      if (ctx.measureText(testLine).width <= maxTextWidth) {
        currentLine = testLine;
      } else {
        if (currentLine) lines.push(currentLine);
        currentLine = word;
      }
    }
    if (currentLine) lines.push(currentLine);
  }

  return lines.length > 0 ? lines.slice(0, 5) : [];
};

const drawHotspotTexture = (ctx, img, hotspot, options = {}) => {
  const width = HOTSPOT_CANVAS_W;
  const height = HOTSPOT_CANVAS_H;
  const style = getHotspotStyle(hotspot);
  ctx.clearRect(0, 0, width, height);

  const centerY = height / 2;
  const iconRadius = style.iconSize / 2;
  const isHidden = Boolean(hotspot?.isHidden ?? hotspot?.is_hidden);
  const isEditor = Boolean(options.isEditor);

  // In public view, hidden hotspots are invisible but keep the pin hit region interactive
  if (isHidden && !isEditor) {
    return {
      circle: { x: style.circleX, y: centerY, radius: style.circleHitRadius },
      line: {
        segments: [],
        hitWidth: 0,
      },
      card: null,
    };
  }

  const rawText = hotspot?.name != null ? String(hotspot.name) : "";
  const hasText = rawText.trim().length > 0;

  if (!hasText) {
    if (img) {
      ctx.drawImage(
        img,
        style.circleX - iconRadius,
        centerY - iconRadius,
        style.iconSize,
        style.iconSize
      );
    }

    return {
      circle: { x: style.circleX, y: centerY, radius: style.circleHitRadius },
      line: {
        segments: [],
        hitWidth: 0,
      },
      card: null,
    };
  }

  const hasLink = isTextHotspot(hotspot) && Boolean(hotspot.webUrl?.trim());
  const linkSpace = hasLink ? 48 : 0;
  const maxTextWidth = Math.max(
    style.cardMinWidth,
    style.cardMaxWidth - style.cardPaddingX * 2 - linkSpace
  );

  ctx.font = `${style.fontWeight} ${style.fontSize}px ${style.fontFamily}`;
  const lines = wrapTextLines(ctx, rawText.trim(), maxTextWidth);

  const maxLineWidth = Math.max(...lines.map(line => ctx.measureText(line).width), 0);
  const cardWidth = Math.min(
    style.cardMaxWidth,
    Math.max(style.cardMinWidth, maxLineWidth + style.cardPaddingX * 2 + linkSpace)
  );

  const lineHeight = 26;
  const lineCount = Math.max(lines.length, 1);
  const cardHeight = style.cardHeight + (lineCount - 1) * lineHeight;

  const lineStartX = style.circleX;
  const lineStartY = centerY;
  const lineElbowX = lineStartX + style.lineInclineX;
  const cardX = lineElbowX + style.lineStraightLength;
  const baseElbowY = lineStartY + style.lineInclineY;

  const cardY = Math.max(10, Math.min(height - cardHeight - 10, baseElbowY - cardHeight / 2));
  const connectorY = THREE.MathUtils.clamp(
    baseElbowY,
    cardY + style.cardRadius,
    cardY + cardHeight - style.cardRadius
  );

  ctx.strokeStyle = style.accentColor;
  ctx.lineWidth = style.lineWidth;
  ctx.beginPath();
  ctx.moveTo(lineStartX, lineStartY);
  ctx.lineTo(lineElbowX, connectorY);
  ctx.lineTo(cardX, connectorY);
  ctx.stroke();

  ctx.save();
  ctx.shadowColor = "rgba(15, 23, 42, 0.18)";
  ctx.shadowBlur = 18;
  ctx.shadowOffsetY = 5;
  if (style.cardGradient) {
    const gradient = ctx.createLinearGradient(
      cardX,
      cardY,
      cardX + cardWidth,
      cardY + cardHeight
    );
    style.cardGradient.forEach((color, index) => {
      gradient.addColorStop(index / Math.max(style.cardGradient.length - 1, 1), color);
    });
    ctx.fillStyle = gradient;
  } else {
    ctx.fillStyle = style.cardBackground;
  }
  roundedRect(ctx, cardX, cardY, cardWidth, cardHeight, style.cardRadius);
  ctx.fill();
  ctx.restore();

  if (style.cardBorderWidth > 0) {
    ctx.strokeStyle = style.cardBorderColor;
    ctx.lineWidth = style.cardBorderWidth;
    roundedRect(ctx, cardX, cardY, cardWidth, cardHeight, style.cardRadius);
    ctx.stroke();
  }

  ctx.fillStyle = style.fontColor;
  ctx.textAlign = "left";
  ctx.textBaseline = "middle";

  const totalTextSpan = (lineCount - 1) * lineHeight;
  const firstLineY = cardY + cardHeight / 2 - totalTextSpan / 2;

  lines.forEach((line, index) => {
    ctx.fillText(
      line,
      cardX + style.cardPaddingX,
      firstLineY + index * lineHeight,
      cardWidth - style.cardPaddingX * 2 - linkSpace
    );
  });

  if (hasLink) {
    drawExternalLinkIcon(
      ctx,
      cardX + cardWidth - style.cardPaddingX - 18,
      cardY + cardHeight / 2 - 10,
      style.accentColor
    );
  }

  if (img) {
    ctx.drawImage(
      img,
      style.circleX - iconRadius,
      centerY - iconRadius,
      style.iconSize,
      style.iconSize
    );
  }

  return {
    circle: { x: style.circleX, y: centerY, radius: style.circleHitRadius },
    line: {
      segments: [
        { x1: lineStartX, y1: lineStartY, x2: lineElbowX, y2: connectorY },
        { x1: lineElbowX, y1: connectorY, x2: cardX, y2: connectorY },
      ],
      hitWidth: style.lineWidth + style.lineHitPadding * 2,
    },
    card: {
      x: cardX,
      y: cardY,
      width: cardWidth,
      height: cardHeight,
      radius: style.cardRadius,
    },
  };
};

const createHotspotTexture = (hotspot = {}, onTextureReady, options = {}) => {
  const canvas = document.createElement("canvas");
  canvas.width = HOTSPOT_CANVAS_W;
  canvas.height = HOTSPOT_CANVAS_H;
  const ctx = canvas.getContext("2d");

  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.minFilter = THREE.LinearFilter;
  texture.magFilter = THREE.LinearFilter;
  texture.generateMipmaps = false;

  if (!ctx) return texture;

  // Paint the card immediately so the first demand-render frame is not blank
  // while the SVG icon is still loading.
  texture.userData.hotspotHitRegions = drawHotspotTexture(ctx, null, hotspot, options);
  texture.needsUpdate = true;

  const isHidden = Boolean(hotspot?.isHidden ?? hotspot?.is_hidden);
  const isEditor = Boolean(options.isEditor);
  if (isHidden && !isEditor) {
    onTextureReady?.();
    return texture;
  }

  const iconSrc = getHotspotStyle(hotspot).iconSrc;
  if (!iconSrc) {
    onTextureReady?.();
    return texture;
  }

  loadHotspotIcon(iconSrc).then(img => {
    texture.userData.hotspotHitRegions = drawHotspotTexture(ctx, img, hotspot, options);
    texture.needsUpdate = true;
    onTextureReady?.();
  });

  return texture;
};

const isPointInRoundedRect = (x, y, rect) => {
  if (
    x < rect.x ||
    x > rect.x + rect.width ||
    y < rect.y ||
    y > rect.y + rect.height
  ) {
    return false;
  }

  const radius = Math.min(rect.radius, rect.width / 2, rect.height / 2);
  const nearestX = THREE.MathUtils.clamp(x, rect.x + radius, rect.x + rect.width - radius);
  const nearestY = THREE.MathUtils.clamp(y, rect.y + radius, rect.y + rect.height - radius);
  return Math.hypot(x - nearestX, y - nearestY) <= radius;
};

const distanceToLineSegment = (x, y, segment) => {
  const dx = segment.x2 - segment.x1;
  const dy = segment.y2 - segment.y1;
  const lengthSquared = dx * dx + dy * dy;
  if (lengthSquared === 0) return Math.hypot(x - segment.x1, y - segment.y1);

  const projection = THREE.MathUtils.clamp(
    ((x - segment.x1) * dx + (y - segment.y1) * dy) / lengthSquared,
    0,
    1
  );
  return Math.hypot(
    x - (segment.x1 + projection * dx),
    y - (segment.y1 + projection * dy)
  );
};

export const isHotspotIntersectionInteractive = intersection => {
  const uv = intersection?.uv;
  const regions = intersection?.object?.material?.map?.userData?.hotspotHitRegions;
  if (!uv || !regions) return false;

  const x = uv.x * HOTSPOT_CANVAS_W;
  const y = (1 - uv.y) * HOTSPOT_CANVAS_H;
  const circleDistance = Math.hypot(x - regions.circle.x, y - regions.circle.y);
  const isOnLine =
    Boolean(regions.line?.segments?.length) &&
    regions.line.segments.some(
      segment => distanceToLineSegment(x, y, segment) <= regions.line.hitWidth / 2
    );
  const isCard = Boolean(regions.card) && isPointInRoundedRect(x, y, regions.card);

  return (
    circleDistance <= regions.circle.radius * 4 ||
    isOnLine ||
    isCard
  );
};

export const createHotspotObject = (hotspot, onTextureReady, options = {}) => {
  const style = getHotspotStyle(hotspot);
  const isEditor = Boolean(options.isEditor);
  const isSelected = Boolean(options.isSelected);
  const isHidden = Boolean(hotspot?.isHidden ?? hotspot?.is_hidden);
  const texture = createHotspotTexture(hotspot, onTextureReady, options);
  
  let initialOpacity = 1.0;
  if (isHidden) {
    if (isEditor) {
      initialOpacity = isSelected ? 0.9 : 0;
    } else {
      initialOpacity = 1.0;
    }
  }

  const material = new THREE.SpriteMaterial({
    map: texture,
    transparent: true,
    depthTest: false,
    depthWrite: false,
    opacity: initialOpacity,
  });
  const sprite = new THREE.Sprite(material);
  sprite.userData.hotspot = hotspot;
  sprite.userData.baseScale = { x: style.spriteWidth, y: style.spriteHeight };
  sprite.userData.isEditor = isEditor;
  sprite.scale.set(style.spriteWidth, style.spriteHeight, 1);
  // Anchor the 3D position to the circle, not the middle of the wide card.
  sprite.center.set(style.circleX / HOTSPOT_CANVAS_W, 0.5);
  sprite.renderOrder = isSelected ? 20 : 10;
  sprite.frustumCulled = false;

  const position = yawPitchToVector3(hotspot.yaw, hotspot.pitch, PANORAMA_RADIUS - 2);
  sprite.position.copy(position);

  const group = new THREE.Group();
  group.add(sprite);
  group.userData.sprite = sprite;
  group.userData.hotspot = hotspot;
  return group;
};

export const updateHotspotSpriteAppearance = (
  sprite,
  { hovered = false, selected = false, pulse = 0 } = {}
) => {
  const base = sprite.userData.baseScale ?? { x: 480, y: 160 };
  let scale = 1 + pulse * 0.05;
  if (selected && hovered) {
    scale = 1.18;
  } else if (selected) {
    scale = 1.12;
  } else if (hovered) {
    scale = 1.08;
  }
  sprite.scale.set(base.x * scale, base.y * scale, 1);

  if (sprite.material) {
    const isHidden = Boolean(
      sprite.userData.hotspot?.isHidden ?? sprite.userData.hotspot?.is_hidden
    );
    const isEditor = Boolean(sprite.userData.isEditor);
    if (isHidden) {
      if (isEditor) {
        sprite.material.opacity = selected ? 0.9 : 0;
      } else {
        sprite.material.opacity = 1.0;
      }
    } else {
      // If it's not hidden, the hotspot must always show at 100% opacity
      sprite.material.opacity = 1.0;
    }
  }
};

const _camDir = new THREE.Vector3();
const _worldPos = new THREE.Vector3();

export const updateHotspotVisibility = (hotspotGroups, camera, is180 = false) => {
  camera.getWorldDirection(_camDir);

  for (const group of hotspotGroups) {
    const sprite = group.userData.sprite;
    if (!sprite) continue;
    const hotspot = group.userData.hotspot;
    if (is180 && hotspot) {
      const yaw = hotspot.yaw ?? 0;
      if (yaw < -Math.PI / 2 - 0.05 || yaw > Math.PI / 2 + 0.05) {
        group.visible = false;
        continue;
      }
    }
    sprite.getWorldPosition(_worldPos);
    _worldPos.normalize();
    group.visible = _camDir.dot(_worldPos) > 0.05;
  }
};

export const disposeHotspotGroups = hotspotGroups => {
  for (const group of hotspotGroups ?? []) {
    const sprite = group.userData.sprite;
    if (sprite?.material) {
      sprite.material.map?.dispose();
      sprite.material.dispose();
    }
    group.removeFromParent?.();
  }
};

export const disposePanoramaResources = ({ geometry, material, texture }) => {
  disposeUncachedTexture(texture);
  geometry?.dispose?.();
  material?.dispose?.();
};

export const disposeUncachedTexture = texture => {
  if (!texture) return;
  if (
    texture.userData?.videoSource &&
    typeof texture.userData.videoSource.dispose === "function"
  ) {
    try {
      texture.userData.videoSource.dispose();
    } catch {
      /* already disposed */
    }
  }
  const image = texture.image;
  if (image && typeof image.close === "function") {
    try {
      image.close();
    } catch {
      /* already closed */
    }
  }
  texture.image = null;
  texture.dispose?.();
};

/**
 * Create a managed video source and THREE.VideoTexture for 360° video scenes.
 *
 * Configures HTMLVideoElement with playsinline, muted, loop, and crossOrigin.
 * Uses requestVideoFrameCallback with a fallback loop for demand-driven rendering.
 * Provides waitForFirstFrame, play, pause, setMuted, and comprehensive disposal.
 */
export const VIDEO_BUFFER_TARGET_SECONDS = 1.2;

export const getContiguousForwardBuffer = video => {
  if (!video || !video.buffered || video.buffered.length === 0) return 0;
  const current = video.currentTime || 0;
  for (let i = 0; i < video.buffered.length; i++) {
    const start = video.buffered.start(i);
    const end = video.buffered.end(i);
    if (start <= current + 0.15 && end >= current) {
      return Math.max(0, end - current);
    }
  }
  return 0;
};

export const hasSufficientForwardBuffer = (
  video,
  targetSeconds = VIDEO_BUFFER_TARGET_SECONDS
) => {
  if (!video) return false;
  if (video.readyState >= 4) return true;
  if (video.ended) return true;

  const duration = video.duration;
  const current = video.currentTime || 0;
  const remaining =
    Number.isFinite(duration) && duration > 0
      ? Math.max(0, duration - current)
      : targetSeconds;

  const required = Math.min(targetSeconds, remaining);
  if (required <= 0.2) return true;

  return getContiguousForwardBuffer(video) >= required;
};

/**
 * Create a managed video source and THREE.VideoTexture for 360° video scenes.
 *
 * Configures HTMLVideoElement with playsinline, muted, loop, and crossOrigin.
 * Uses requestVideoFrameCallback with a fallback loop for demand-driven rendering.
 * Provides waitForFirstFrame, play, pause, setMuted, and comprehensive disposal.
 */
export const createPanoramaVideoSource = (
  videoUrl,
  {
    onFrame,
    onWaiting,
    onPlaying,
    onError,
    onEnded,
    loop = true,
    muted = true,
    signal,
    timeoutMs = 20000,
  } = {}
) => {
  const video = document.createElement("video");
  video.playsInline = true;
  video.setAttribute("playsinline", "");
  video.setAttribute("webkit-playsinline", "");
  video.muted = muted;
  video.defaultMuted = muted;
  video.loop = loop;
  video.preload = "auto";
  video.autoplay = false;

  if (videoUrl && !videoUrl.startsWith("blob:") && !videoUrl.startsWith("data:")) {
    video.crossOrigin = "anonymous";
  }

  const texture = new THREE.VideoTexture(video);
  // The pixels remain sRGB encoded. createPanoramaMaterial performs the decode
  // exactly once; NoColorSpace here prevents r152's hardware sRGB upload path.
  texture.colorSpace = THREE.NoColorSpace;
  texture.userData.panoramaVideoSRGBDecode = true;
  texture.minFilter = THREE.LinearFilter;
  texture.magFilter = THREE.LinearFilter;
  texture.generateMipmaps = false;
  texture.flipY = true;
  texture.userData.isVideoTexture = true;
  texture.userData.uncachedFallback = true;

  let frameCallbackId = null;
  let rafFallbackId = null;
  let isDisposed = false;
  let isLoopRunning = false;
  let schedulerGen = 0;
  let lastFallbackTime = 0;
  let isBufferingState = false;

  const setBufferingState = isBuffering => {
    if (isDisposed) return;
    if (isBufferingState !== isBuffering) {
      isBufferingState = isBuffering;
      onWaiting?.(isBuffering);
    }
  };

  const cancelPendingCallbacks = () => {
    if (frameCallbackId != null && typeof video.cancelVideoFrameCallback === "function") {
      try {
        video.cancelVideoFrameCallback(frameCallbackId);
      } catch {
        /* ignore */
      }
      frameCallbackId = null;
    }
    if (rafFallbackId != null) {
      try {
        cancelAnimationFrame(rafFallbackId);
      } catch {
        /* ignore */
      }
      rafFallbackId = null;
    }
  };

  const stopFrameLoop = () => {
    isLoopRunning = false;
    schedulerGen++;
    cancelPendingCallbacks();
  };

  const scheduleNext = gen => {
    if (isDisposed || !isLoopRunning || gen !== schedulerGen || video.paused || video.ended) {
      isLoopRunning = false;
      return;
    }

    cancelPendingCallbacks();

    if (typeof video.requestVideoFrameCallback === "function") {
      try {
        frameCallbackId = video.requestVideoFrameCallback((now, metadata) => {
          frameCallbackId = null;
          if (isDisposed || !isLoopRunning || gen !== schedulerGen || video.paused || video.ended) {
            isLoopRunning = false;
            return;
          }
          onFrame?.(now, metadata);
          scheduleNext(gen);
        });
      } catch {
        frameCallbackId = null;
        isLoopRunning = false;
      }
    } else {
      rafFallbackId = requestAnimationFrame(now => {
        rafFallbackId = null;
        if (isDisposed || !isLoopRunning || gen !== schedulerGen || video.paused || video.ended) {
          isLoopRunning = false;
          return;
        }
        if (now - lastFallbackTime >= 30) {
          lastFallbackTime = now;
          onFrame?.(now);
        }
        scheduleNext(gen);
      });
    }
  };

  const startFrameLoop = () => {
    if (isDisposed || video.paused || video.ended) return;
    if (isLoopRunning && (frameCallbackId != null || rafFallbackId != null)) {
      return;
    }

    isLoopRunning = true;
    const currentGen = ++schedulerGen;
    scheduleNext(currentGen);
  };

  const handleWaiting = () => {
    setBufferingState(true);
  };

  const handleStalled = () => {
    if (!video.paused && !video.ended && !hasSufficientForwardBuffer(video, 0.4)) {
      setBufferingState(true);
    }
  };

  const handleProgress = () => {
    if (isBufferingState && hasSufficientForwardBuffer(video, VIDEO_BUFFER_TARGET_SECONDS)) {
      setBufferingState(false);
    }
  };

  const handleCanPlay = () => {
    if (isBufferingState) {
      if (hasSufficientForwardBuffer(video, VIDEO_BUFFER_TARGET_SECONDS)) {
        setBufferingState(false);
      }
    } else {
      setBufferingState(false);
    }
  };

  const handleCanPlayThrough = () => {
    setBufferingState(false);
  };

  const handlePlaying = () => {
    setBufferingState(false);
    onPlaying?.();
    startFrameLoop();
  };

  const handlePlay = () => {
    startFrameLoop();
  };

  const handlePause = () => {
    stopFrameLoop();
  };

  const handleSeeked = () => {
    if (!video.paused && !video.ended) {
      startFrameLoop();
    }
    onFrame?.();
  };

  const handleEnded = () => {
    stopFrameLoop();
    onEnded?.();
  };

  const handleError = e => {
    stopFrameLoop();
    onError?.(e);
  };

  video.addEventListener("play", handlePlay);
  video.addEventListener("playing", handlePlaying);
  video.addEventListener("pause", handlePause);
  video.addEventListener("waiting", handleWaiting);
  video.addEventListener("stalled", handleStalled);
  video.addEventListener("progress", handleProgress);
  video.addEventListener("canplay", handleCanPlay);
  video.addEventListener("canplaythrough", handleCanPlayThrough);
  video.addEventListener("seeked", handleSeeked);
  video.addEventListener("ended", handleEnded);
  video.addEventListener("error", handleError);

  const waitForFirstFrame = () =>
    new Promise((resolve, reject) => {
      if (isDisposed) {
        return reject(
          Object.assign(new Error("Video source disposed"), { name: "AbortError" })
        );
      }
      if (signal?.aborted) {
        return reject(Object.assign(new Error("Aborted"), { name: "AbortError" }));
      }

      let hardTimeoutTimer = null;
      let bufferGraceTimer = null;
      let hasDecodedFirstFrame = false;

      const cleanup = () => {
        if (hardTimeoutTimer) clearTimeout(hardTimeoutTimer);
        if (bufferGraceTimer) clearTimeout(bufferGraceTimer);
        signal?.removeEventListener?.("abort", onAbort);
        video.removeEventListener("loadeddata", checkReady);
        video.removeEventListener("canplay", checkReady);
        video.removeEventListener("canplaythrough", checkReady);
        video.removeEventListener("progress", checkReady);
        video.removeEventListener("timeupdate", checkReady);
        video.removeEventListener("error", onFail);
      };

      const onAbort = () => {
        cleanup();
        reject(Object.assign(new Error("Aborted"), { name: "AbortError" }));
      };
      signal?.addEventListener?.("abort", onAbort, { once: true });

      const onFail = () => {
        cleanup();
        const mediaError = video.error;
        let msg = "Could not play 360 video.";
        if (mediaError?.code === MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED) {
          msg = "Video format or codec not supported.";
        }
        reject(new Error(msg));
      };

      const finishSuccess = () => {
        cleanup();
        resolve({ video, texture });
      };

      const checkReady = () => {
        if (isDisposed || signal?.aborted) {
          cleanup();
          return reject(Object.assign(new Error("Aborted"), { name: "AbortError" }));
        }

        const hasDimensions = video.videoWidth > 0 && video.videoHeight > 0;
        const hasFirstFrame = video.readyState >= 2 && hasDimensions;

        if (!hasFirstFrame) {
          return;
        }

        if (!hasDecodedFirstFrame) {
          hasDecodedFirstFrame = true;
          // Set soft grace timer so startup never stalls indefinitely
          bufferGraceTimer = setTimeout(() => {
            finishSuccess();
          }, 3500);
        }

        if (hasSufficientForwardBuffer(video, VIDEO_BUFFER_TARGET_SECONDS)) {
          finishSuccess();
        }
      };

      video.addEventListener("loadeddata", checkReady);
      video.addEventListener("canplay", checkReady);
      video.addEventListener("canplaythrough", checkReady);
      video.addEventListener("progress", checkReady);
      video.addEventListener("timeupdate", checkReady);
      video.addEventListener("error", onFail, { once: true });

      checkReady();

      if (timeoutMs > 0) {
        hardTimeoutTimer = setTimeout(() => {
          cleanup();
          if (hasDecodedFirstFrame) {
            resolve({ video, texture });
          } else {
            reject(new Error("Timeout waiting for 360 video stream"));
          }
        }, timeoutMs);
      }
    });

  const play = async () => {
    if (isDisposed) return false;
    try {
      await video.play();
      startFrameLoop();
      return true;
    } catch (err) {
      if (err?.name === "AbortError") {
        return false;
      }
      return false;
    }
  };

  const pause = () => {
    if (isDisposed) return;
    video.pause();
    stopFrameLoop();
  };

  const setMuted = isMuted => {
    if (isDisposed) return;
    video.muted = isMuted;
    video.defaultMuted = isMuted;
  };

  const isPaused = () => video.paused;
  const isMuted = () => video.muted;

  const dispose = () => {
    if (isDisposed) return;
    isDisposed = true;
    stopFrameLoop();
    video.removeEventListener("play", handlePlay);
    video.removeEventListener("playing", handlePlaying);
    video.removeEventListener("pause", handlePause);
    video.removeEventListener("waiting", handleWaiting);
    video.removeEventListener("stalled", handleStalled);
    video.removeEventListener("progress", handleProgress);
    video.removeEventListener("canplay", handleCanPlay);
    video.removeEventListener("canplaythrough", handleCanPlayThrough);
    video.removeEventListener("seeked", handleSeeked);
    video.removeEventListener("ended", handleEnded);
    video.removeEventListener("error", handleError);
    try {
      video.pause();
      video.removeAttribute("src");
      video.load();
    } catch {
      /* ignore */
    }
    texture.image = null;
    texture.dispose();
  };

  const controller = {
    video,
    texture,
    waitForFirstFrame,
    play,
    pause,
    setMuted,
    isPaused,
    isMuted,
    isBuffering: () => isBufferingState,
    hasSufficientBuffer: (targetSec = VIDEO_BUFFER_TARGET_SECONDS) =>
      hasSufficientForwardBuffer(video, targetSec),
    getForwardBuffer: () => getContiguousForwardBuffer(video),
    isSchedulingActive: () => isLoopRunning,
    dispose,
  };

  texture.userData.videoSource = controller;

  try {
    video.src = videoUrl;
    video.load();
  } catch {
    /* ignore */
  }

  return controller;
};


const sceneYaw = sceneData =>
  THREE.MathUtils.degToRad(sceneData?.defaultView?.yaw || 0);

export const getSceneYaw = sceneYaw;

const configureTexture = image => {
  const texture = new THREE.Texture(image);
  texture.wrapS = THREE.ClampToEdgeWrapping;
  texture.wrapT = THREE.ClampToEdgeWrapping;
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.minFilter = THREE.LinearFilter;
  texture.magFilter = THREE.LinearFilter;
  texture.generateMipmaps = false;
  texture.flipY = true;
  texture.needsUpdate = true;
  return texture;
};

const closeSource = source => {
  if (typeof source?.close === "function") source.close();
};

const sourceToCanvas = (source, width, height) => {
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  ctx?.drawImage(source, 0, 0, width, height);
  closeSource(source);
  return canvas;
};

const downscaleSource = async source => {
  const maxW = getMaxPanoramaWidth({
    deviceMemory: typeof navigator !== "undefined" ? navigator.deviceMemory : undefined,
    innerWidth: typeof window !== "undefined" ? window.innerWidth : undefined,
  });
  const width = source.width;
  const height = source.height;
  if (!width) return configureTexture(sourceToCanvas(source, 2, 1));

  const needsResize = width > maxW;
  const targetW = needsResize ? Math.round(maxW) : width;
  const targetH = needsResize ? Math.round((height * maxW) / width) : height;
  const isBitmap = typeof ImageBitmap !== "undefined" && source instanceof ImageBitmap;

  if (isBitmap || needsResize) {
    return configureTexture(sourceToCanvas(source, targetW, targetH));
  }

  return configureTexture(source);
};

const loadHtmlImage = (url, crossOrigin, signal) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    const cleanup = () => {
      signal?.removeEventListener?.("abort", onAbort);
      image.onload = null;
      image.onerror = null;
    };
    const onAbort = () => {
      cleanup();
      image.src = "";
      reject(Object.assign(new Error("Aborted"), { name: "AbortError" }));
    };
    if (signal?.aborted) {
      onAbort();
      return;
    }
    signal?.addEventListener?.("abort", onAbort);
    if (crossOrigin) image.crossOrigin = crossOrigin;
    image.decoding = "async";
    image.onload = () => {
      cleanup();
      resolve(image);
    };
    image.onerror = () => {
      cleanup();
      reject(new Error(`Failed to load panorama: ${url}`));
    };
    image.src = url;
  });

const bitmapFromBlob = async (blob, signal) => {
  if (typeof createImageBitmap !== "function") return null;
  if (signal?.aborted) {
    throw Object.assign(new Error("Aborted"), { name: "AbortError" });
  }
  try {
    return await createImageBitmap(blob, {
      imageOrientation: "from-image",
      colorSpaceConversion: "none",
      premultiplyAlpha: "none",
    });
  } catch {
    try {
      return await createImageBitmap(blob);
    } catch {
      return null;
    }
  }
};

export const decodePanorama = async (url, { signal } = {}) => {
  if (url.startsWith("blob:") || url.startsWith("data:")) {
    const image = await loadHtmlImage(url, null, signal);
    return downscaleSource(image);
  }

  try {
    const response = await fetch(url, {
      mode: "cors",
      credentials: "omit",
      signal,
    });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const blob = await response.blob();
    if (signal?.aborted) {
      throw Object.assign(new Error("Aborted"), { name: "AbortError" });
    }
    const bitmap = await bitmapFromBlob(blob, signal);
    if (bitmap) return downscaleSource(bitmap);
    const objectUrl = URL.createObjectURL(blob);
    try {
      const image = await loadHtmlImage(objectUrl, null, signal);
      return downscaleSource(image);
    } finally {
      URL.revokeObjectURL(objectUrl);
    }
  } catch (err) {
    if (err?.name === "AbortError") throw err;
    try {
      const image = await loadHtmlImage(url, "anonymous", signal);
      return downscaleSource(image);
    } catch (inner) {
      if (inner?.name === "AbortError") throw inner;
      const separator = url.includes("?") ? "&" : "?";
      try {
        const image = await loadHtmlImage(
          `${url}${separator}v=360`,
          "anonymous",
          signal
        );
        return downscaleSource(image);
      } catch (last) {
        if (last?.name === "AbortError") throw last;
        const image = await loadHtmlImage(url, null, signal);
        return downscaleSource(image);
      }
    }
  }
};

const releaseCpuSource = texture => {
  const image = texture?.image;
  if (!image) return;
  if (typeof image.close === "function") {
    try {
      image.close();
    } catch {
      /* closed */
    }
  }
  if (image instanceof HTMLCanvasElement) {
    image.width = 1;
    image.height = 1;
  }
  texture.image = null;
};

let runtime;

export const getPanoramaRuntime = () => {
  if (!runtime) {
    runtime = createPanoramaRuntime({
      loadTexture: decodePanorama,
      getBudget: () =>
        getTextureByteBudget({
          deviceMemory:
            typeof navigator !== "undefined" ? navigator.deviceMemory : undefined,
          innerWidth: typeof window !== "undefined" ? window.innerWidth : undefined,
        }),
      estimateBytes: texture =>
        estimateTextureBytes(texture?.image?.width, texture?.image?.height, {
          generateMipmaps: Boolean(texture?.generateMipmaps),
        }),
      disposeTexture: texture => {
        releaseCpuSource(texture);
        texture?.dispose?.();
      },
    });
  }
  return runtime;
};

export const loadPanoramaTexture = (imageUrl, options = {}) =>
  getPanoramaRuntime().request(imageUrl, options);

export const prefetchPanoramaTextures = (
  urls = [],
  { ownerId, priority, speculative = true } = {}
) => {
  const runtimeApi = getPanoramaRuntime();
  urls.filter(Boolean).forEach(url => {
    runtimeApi.request(url, { ownerId, priority, speculative }).catch(() => {});
  });
};

export const createFallbackPanoramaTexture = (sceneId, sceneName) => {
  const canvas = document.createElement("canvas");
  canvas.width = 2048;
  canvas.height = 1024;
  const ctx = canvas.getContext("2d");
  if (ctx) {
    const gradient = ctx.createLinearGradient(0, 0, canvas.width, canvas.height);
    gradient.addColorStop(0, "#0b1e3a");
    gradient.addColorStop(1, "#050a1f");
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = "#00f0ff";
    ctx.font = "bold 48px Outfit, sans-serif";
    ctx.textAlign = "center";
    ctx.fillText(sceneName || "Panorama", canvas.width / 2, canvas.height / 2);
    ctx.font = "24px Outfit, sans-serif";
    ctx.fillStyle = "#a0aec0";
    ctx.fillText(sceneId || "", canvas.width / 2, canvas.height / 2 + 48);
  }
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.minFilter = THREE.LinearFilter;
  texture.generateMipmaps = false;
  texture.userData.uncachedFallback = true;
  return texture;
};

export { TOUR_PERF as tourPerfDefaults } from "./panoramaPerf";
